import { ACCESSORIES, AccessoryData } from './accessories';

export interface StageData {
  id: string;
  index: number; // 1..N (display order)
  name: string;
  accessoryId: string;
  bpm: number;
  maxAngle: number;
  throwLimit: number;
  // Multiplier applied to HIT_LATERAL_* / HIT_FORCE_*_MULT thresholds (config.ts).
  // 1.0 = Stage 1 baseline; lower = stricter window.
  hitWindowMult: number;
  // Optional dance-motion params. Stage 1 omits both → static (easy mode).
  swayLateralPx?: number;
  swayDepthPx?: number;
  swayPhaseRatio?: number;
}

// Score per throw outcome.
export const THROW_SCORE = {
  perfect: 100,
  good: 50,
  just: 25,
  miss: 0,
} as const;

// Star count by hit quality. PERFECT = 3, GOOD = 2, JUST = 1.
export function starsForHitType(hitType: 'perfect' | 'good' | 'just'): number {
  if (hitType === 'perfect') return 3;
  if (hitType === 'good') return 2;
  return 1;
}

export const STAGES: StageData[] = [
  {
    id: 'stage1',
    index: 1,
    name: 'Cowboy Stage',
    accessoryId: 'cowboyhat',
    bpm: 90,
    maxAngle: 5,
    throwLimit: 3,
    hitWindowMult: 1.0,
  },
  {
    id: 'stage2',
    index: 2,
    name: 'Lion Stage',
    accessoryId: 'lion',
    bpm: 100,
    maxAngle: 6,
    throwLimit: 3,
    hitWindowMult: 0.85,
    swayLateralPx: 25,
  },
  {
    id: 'stage3',
    index: 3,
    name: 'Rabbit Stage',
    accessoryId: 'rabbit',
    bpm: 110,
    maxAngle: 7,
    throwLimit: 3,
    hitWindowMult: 0.7,
    swayLateralPx: 40,
    swayDepthPx: 18,
  },
  {
    id: 'stage4',
    index: 4,
    name: 'Sunglasses Stage',
    accessoryId: 'sunglass',
    bpm: 120,
    maxAngle: 7,
    throwLimit: 3,
    hitWindowMult: 0.55,
    swayLateralPx: 55,
    swayDepthPx: 28,
  },
  {
    id: 'stage5',
    index: 5,
    name: 'Sushi Stage',
    accessoryId: 'susi',
    bpm: 130,
    maxAngle: 8,
    throwLimit: 3,
    hitWindowMult: 0.5,
    swayLateralPx: 70,
    swayDepthPx: 40,
  },
];

export function getStage(id: string): StageData | undefined {
  return STAGES.find((s) => s.id === id);
}

export function getAccessoryForStage(stage: StageData): AccessoryData {
  const acc = ACCESSORIES[stage.accessoryId];
  if (!acc) throw new Error(`Stage ${stage.id} references unknown accessory ${stage.accessoryId}`);
  return acc;
}

export function scoreForResult(result: 'perfect' | 'good' | 'just' | 'miss'): number {
  if (result === 'perfect') return THROW_SCORE.perfect;
  if (result === 'good') return THROW_SCORE.good;
  if (result === 'just') return THROW_SCORE.just;
  return 0;
}
